import Cookies from "js-cookie";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { CoreKeys } from "@intelligems/ig-plugin/core";
import { Launch } from "@src/components/Launch/Launch";
import { ShareRoute } from "@src/features/shareResults/Share.route";
import { useRouteTiming } from "@src/hooks/timing";
import { setInitialTimingPath } from "@src/store/slices/global";
import { IgStorage } from "@src/utils/igStorage";
import { useIsMutating } from "@tanstack/react-query";

import { userKeys } from "../api/queryKeys";
import { useAuth } from "../hooks/auth";
import Loading from "../utils/Loading";

import { LoggedInRoutes } from "./loggedIn";
import { PublicRoutes } from "./public";

export const AppRoutes = () => {
  const user = useAuth();
  const isLoggingIn = useIsMutating({ mutationKey: userKeys.all });
  const dispatch = useDispatch();

  useEffect(() => {
    window.enterBeta = () => {
      Cookies.set("ig-beta", "true", {
        expires: CoreKeys.ID_COOKIE_DAYS_TO_LIVE,
        domain: "." + window.location.hostname,
      });
    };
    window.exitBeta = () => {
      Cookies.remove("ig-beta");
    };
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const clear = params.get("clear");
    if (clear) {
      IgStorage.clearAuthTokens();
      url.searchParams.delete("clear");
      location.href = url.href;
    }

    const invalidAccessToken = params.get("invalidAccessToken");
    if (invalidAccessToken) {
      toast.error(
        "Shopify access token is invalid.  Please uninstall and reinstall the Intelligems app."
      );
    }
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    dispatch(setInitialTimingPath(path));
  }, []);

  useRouteTiming({
    page: "init",
    isReady: true,
  });

  const isLoggedIn = useMemo(
    () => user.isSuccess && user.data.type === "login" && user.data.user,
    [user.isSuccess, user.data]
  );

  if (window.location.pathname === "/launch") {
    return <Launch />;
  }

  if (window.location.pathname.startsWith("/share")) {
    return <ShareRoute />;
  }

  if (isLoggingIn || user.isLoading) {
    return <Loading centered={true} />;
  }

  if (isLoggedIn) {
    return <LoggedInRoutes />;
  }

  return <PublicRoutes />;
};
